import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, NavDropdown, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartBar,
    faFileAlt,
    faCog,
    faUsersCog,
    faCloudUploadAlt,
    faPlusSquare,
    faUser,
    faUserSecret,
    faKey,
    faExchangeAlt,
    faSignOutAlt,
    faSignInAlt,
    faLayerGroup,
    faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@store/auth.store';
import { useSubscriberStore } from '@store/subscriber.store';
import { useToastStore } from '@store/toast.store';
import { ROLE_ADMIN, AUTHORITIES_ADMIN, STORAGE_KEYS } from '@config/constants';
import { Subscriber } from '@appTypes/subscriber.types';
import fluentLogo from '@assets/images/fluent_logo.png';
import './Navbar.scss';

const Navigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user, logout, hasAuthority, hasAnyAuthority, fetchIdentity } = useAuthStore();
    const { currentSubscriber, subscribers, subscriberId, loadSubscribers, loadCurrentSubscriber, setSubscriberId } =
        useSubscriberStore();
    const addToast = useToastStore((s) => s.addToast);
    const [isNavExpanded, setIsNavExpanded] = useState(false);

    const isAdmin = hasAuthority(ROLE_ADMIN);
    const isAdminOrUserAdmin = hasAnyAuthority(AUTHORITIES_ADMIN);

    // Show toast after subscriber switch reload
    useEffect(() => {
        const switched = sessionStorage.getItem(STORAGE_KEYS.SUBSCRIBER_SWITCHED);
        if (switched) {
            sessionStorage.removeItem(STORAGE_KEYS.SUBSCRIBER_SWITCHED);
            addToast(`Switched to ${switched}`, 'success');
        }
    }, [addToast]);

    // Load identity, subscribers, and current subscriber on mount and route changes
    useEffect(() => {
        const initData = async () => {
            // Fetch identity if not already loaded
            const identity = await fetchIdentity();
            if (identity) {
                // Load current subscriber
                loadCurrentSubscriber();

                // Load all subscribers for admin users
                if (identity.authorities.includes(ROLE_ADMIN)) {
                    loadSubscribers();
                }
            }
        };

        initData();
    }, [location.pathname, fetchIdentity, loadCurrentSubscriber, loadSubscribers]);

    const handleLogout = async () => {
        collapseNavbar();
        await logout();
        navigate('/');
    };

    const handleSubscriberChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newId = Number(event.target.value);
        const selected = subscribers.find((s: Subscriber) => s.id === newId);
        if (selected) {
            sessionStorage.setItem(STORAGE_KEYS.SUBSCRIBER_SWITCHED, selected.abbreviation);
        }
        setSubscriberId(newId, true); // true = reload page
    };

    const collapseNavbar = () => {
        setIsNavExpanded(false);
    };

    const isGroupActive = (prefix: string) => location.pathname.startsWith(prefix);

    return (
        <Navbar variant="dark" expand="md" expanded={isNavExpanded} className="jh-navbar">
            <Container fluid>
                <Navbar.Brand as={Link} to="/" onClick={collapseNavbar}>
                    <img src={fluentLogo} alt="Fluent Energy" className="logo-img" />
                    <span className="navbar-title">
                        <span className="product-name product-prefix">fluent</span>
                        <span className="product-name product-suffix">insight</span>
                    </span>
                    <span className="navbar-version">v{__APP_VERSION__}</span>
                </Navbar.Brand>

                <Navbar.Toggle
                    aria-controls="navbar-collapse"
                    onClick={() => setIsNavExpanded(!isNavExpanded)}
                />

                <Navbar.Collapse id="navbar-collapse" className="justify-content-start">
                    <Nav>
                        {isAuthenticated && (
                            <>
                                {/* Reports Dropdown - Consolidated */}
                                <NavDropdown
                                    title={
                                        <span>
                                            <FontAwesomeIcon icon={faChartBar} fixedWidth />
                                            <span className="nav-text">Reports</span>
                                        </span>
                                    }
                                    id="reports-dropdown"
                                    className={`nav-dropdown-primary${isGroupActive('/static-reports') || isGroupActive('/account-level') || isGroupActive('/group-level') ? ' active-group' : ''}`}
                                >
                                    <NavDropdown.Header>Static</NavDropdown.Header>
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/static-reports"
                                        onClick={collapseNavbar}
                                        id="static-reports-menu"
                                    >
                                        <FontAwesomeIcon icon={faFileAlt} fixedWidth />
                                        <span>File Reports</span>
                                    </NavDropdown.Item>

                                    <NavDropdown.Divider />

                                    <NavDropdown.Header>Dynamic</NavDropdown.Header>
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/account-level"
                                        onClick={collapseNavbar}
                                        id="account-level-menu"
                                    >
                                        <FontAwesomeIcon icon={faBuilding} fixedWidth />
                                        <span>Account Level</span>
                                    </NavDropdown.Item>
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/group-level"
                                        onClick={collapseNavbar}
                                        id="group-level-menu"
                                    >
                                        <FontAwesomeIcon icon={faLayerGroup} fixedWidth />
                                        <span>Group Level</span>
                                    </NavDropdown.Item>
                                </NavDropdown>

                                {/* Client Settings Dropdown (AUTHORITIES_ADMIN) */}
                                {isAdminOrUserAdmin && (
                                    <NavDropdown
                                        title={
                                            <span>
                                                <FontAwesomeIcon icon={faUsersCog} fixedWidth />
                                                <span className="nav-text">Settings</span>
                                            </span>
                                        }
                                        id="client-settings-dropdown"
                                    >
                                        <NavDropdown.Item
                                            as={Link}
                                            to="/user-admin"
                                            onClick={collapseNavbar}
                                            id="user-admin"
                                        >
                                            <FontAwesomeIcon icon={faUser} fixedWidth />
                                            <span>User Admin</span>
                                        </NavDropdown.Item>
                                    </NavDropdown>
                                )}

                                {/* File Upload (ROLE_ADMIN only) */}
                                {isAdmin && (
                                    <Nav.Link
                                        as={Link}
                                        to="/file-upload"
                                        onClick={collapseNavbar}
                                        id="file-upload"
                                    >
                                        <FontAwesomeIcon icon={faCloudUploadAlt} fixedWidth />
                                        <span className="nav-text">Upload</span>
                                    </Nav.Link>
                                )}

                                {/* Account Enrollment (conditional on subscriber.enrollmentHtml) */}
                                {currentSubscriber?.enrollmentHtml && (
                                    <Nav.Link
                                        as={Link}
                                        to="/account-enrollment"
                                        onClick={collapseNavbar}
                                    >
                                        <FontAwesomeIcon icon={faPlusSquare} fixedWidth />
                                        <span className="nav-text">Enroll</span>
                                    </Nav.Link>
                                )}

                                {/* User Account Dropdown */}
                                <NavDropdown
                                    title={
                                        <span>
                                            <FontAwesomeIcon icon={faUser} fixedWidth />
                                            <span className="nav-text">{user?.login}</span>
                                        </span>
                                    }
                                    id="account-menu"
                                    className="nav-dropdown-account"
                                    autoClose={!isAdmin} // Keep open for admin to use subscriber dropdown
                                >
                                    {/* Settings */}
                                    <NavDropdown.Item as={Link} to="/settings" onClick={collapseNavbar}>
                                        <FontAwesomeIcon icon={faCog} fixedWidth />
                                        <span>Settings</span>
                                    </NavDropdown.Item>

                                    {/* Password */}
                                    <NavDropdown.Item as={Link} to="/password" onClick={collapseNavbar}>
                                        <FontAwesomeIcon icon={faKey} fixedWidth />
                                        <span>Password</span>
                                    </NavDropdown.Item>

                                    {/* Impersonate (AUTHORITIES_ADMIN) */}
                                    {isAdminOrUserAdmin && (
                                        <NavDropdown.Item as={Link} to="/impersonate" onClick={collapseNavbar}>
                                            <FontAwesomeIcon icon={faUserSecret} fixedWidth />
                                            <span>Impersonate</span>
                                        </NavDropdown.Item>
                                    )}

                                    {/* Subscriber Switcher (ROLE_ADMIN only) */}
                                    {isAdmin && subscribers.length > 0 && (
                                        <NavDropdown.Item as="div" className="subscriber-dropdown">
                                            <FontAwesomeIcon icon={faExchangeAlt} fixedWidth />
                                            <span>Subscriber</span>
                                            <select
                                                className="form-select form-select-sm mt-2"
                                                value={subscriberId ?? ''}
                                                onChange={handleSubscriberChange}
                                            >
                                                {subscribers.map((sub: Subscriber) => (
                                                    <option key={sub.id} value={sub.id}>
                                                        {sub.abbreviation}
                                                    </option>
                                                ))}
                                            </select>
                                        </NavDropdown.Item>
                                    )}

                                    <NavDropdown.Divider />

                                    {/* Sign Out */}
                                    <NavDropdown.Item onClick={handleLogout} id="logout" className="logout-item">
                                        <FontAwesomeIcon icon={faSignOutAlt} fixedWidth />
                                        <span>Sign out</span>
                                    </NavDropdown.Item>
                                </NavDropdown>
                            </>
                        )}

                        {/* Sign In link (when not authenticated) */}
                        {!isAuthenticated && (
                            <Nav.Link as={Link} to="/login" id="login">
                                <FontAwesomeIcon icon={faSignInAlt} fixedWidth />
                                <span className="nav-text">Sign in</span>
                            </Nav.Link>
                        )}
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};

export default Navigation;
